// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {UserCircleIcon} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {FluxerTagChangeModal} from '@app/features/user/components/modals/FluxerTagChangeModal';
import type {User} from '@app/features/user/models/User';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const CLAIM_YOUR_ACCOUNT_TO_CHANGE_YOUR_USERNAME_DESCRIPTOR = msg({
	message: 'Claim your account to change your username',
	comment: 'Label in the username section.',
});
const VERIFY_YOUR_EMAIL_TO_CHANGE_YOUR_USERNAME_DESCRIPTOR = msg({
	message: 'Verify your email to change your username',
	comment: 'Label in the username section.',
});

interface UsernameSectionProps {
	isClaimed: boolean;
	isEmailVerified: boolean;
	user: User;
}

export const UsernameSection = observer(({isClaimed, isEmailVerified, user}: UsernameSectionProps) => {
	const {i18n} = useLingui();
	const changeButton = (disabled: boolean, dataFlx: string, onClick?: () => void) => (
		<Button variant="secondary" small disabled={disabled} onClick={onClick} data-flx={dataFlx}>
			<Trans>Change username</Trans>
		</Button>
	);
	return (
		<SettingsRow
			icon={<UserCircleIcon size={20} />}
			title={<Trans>Username</Trans>}
			description={user.tag}
			control={() =>
				!isClaimed ? (
					<Tooltip
						text={i18n._(CLAIM_YOUR_ACCOUNT_TO_CHANGE_YOUR_USERNAME_DESCRIPTOR)}
						data-flx="user.my-profile-tab.username-section.tooltip"
					>
						<div data-flx="user.my-profile-tab.username-section.div--2">
							{changeButton(true, 'user.my-profile-tab.username-section.button')}
						</div>
					</Tooltip>
				) : !isEmailVerified ? (
					<Tooltip
						text={i18n._(VERIFY_YOUR_EMAIL_TO_CHANGE_YOUR_USERNAME_DESCRIPTOR)}
						data-flx="user.my-profile-tab.username-section.tooltip--2"
					>
						<div data-flx="user.my-profile-tab.username-section.div--3">
							{changeButton(true, 'user.my-profile-tab.username-section.button--2')}
						</div>
					</Tooltip>
				) : (
					changeButton(false, 'user.my-profile-tab.username-section.button.push', () =>
						ModalCommands.push(
							modal(() => (
								<FluxerTagChangeModal
									user={user}
									data-flx="user.my-profile-tab.username-section.fluxer-tag-change-modal"
								/>
							)),
						),
					)
				)
			}
			data-flx="user.my-profile-tab.username-section.row"
		/>
	);
});
